// EL PUNTO DE "TIENES ALGO QUE FIRMAR", encima de tu foto.
//
// QUE PROBLEMA RESUELVE. Un contrato, un anexo o un protocolo obligatorio se sube al tablón y ahí se
// queda hasta que la persona entra en Mi perfil — que es una pantalla en la que se entra tres veces
// al año. El único aviso que había vivía en la entrada «Tablón» del menú lateral, y ese badge (a) no
// lo ve quien no tiene esa entrada en su menú (doctor, agente) ni existe dentro del panel de
// administración, y (b) cuenta también lo NO LEÍDO: una circular sin abrir lo enciende igual que un
// contrato sin firmar, así que no se puede prometer con él que haya algo que firmar.
//
// QUÉ CUENTA ESTE, ENTONCES: SOLO lo que espera MI FIRMA (`por_firmar` de GET /tablon/contador), y
// eso incluye los documentos que van a todo el equipo o a mi rol, no solo los que llevan mi nombre.
// El badge del menú se queda exactamente como estaba.
//
// POR QUÉ ÁMBAR Y NO ROJO. A cuatro dedos de aquí está la campanita de avisos con su punto rojo. Dos
// puntos rojos idénticos y pegados se leen como un aviso duplicado; en ámbar —el mismo color con el
// que "Mis documentos" marca lo pendiente de firmar— se distinguen de un vistazo y significan lo
// mismo en las dos pantallas.
//
// DÓNDE VA. Envuelve al avatar, sin tocar el avatar: cada pantalla sigue pintando el suyo como lo
// pintaba. Y va en TODOS los sitios donde se pinta la foto del usuario, porque un aviso que solo
// sale en una pantalla es un aviso que se ve cuando ya se había entrado a mirar.
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'
import { t } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// LA MISMA CLAVE QUE EL MENÚ LATERAL (Layout.jsx). React Query comparte la caché por clave, así que
// cinco avatares y el badge del menú son UNA sola petición cada minuto, no seis. Y al firmar,
// MisDocumentos invalida esta misma clave: el punto se apaga solo en todas las pantallas.
//
// UNA SOLA CONSULTA, DOS NÚMEROS. `por_firmar` y `nuevos` salen de la MISMA respuesta y de la misma
// clave: dos `useQuery` con la misma clave en React Query son una sola petición, no dos.
function useContadorTablon() {
  const { data } = useQuery({
    queryKey: ['tablon-pendientes'],
    // El `catch` es el de siempre: un badge jamás rompe una cabecera. Sin la migración de
    // privados+firma el servidor devuelve 0 y aquí no aparece nada.
    queryFn: () => api.get('/tablon/contador').then(r => r.data).catch(() => ({ pendientes: 0, por_firmar: 0, nuevos: 0 })),
    refetchInterval: 60000,
  })
  return data
}

export function usePorFirmar() {
  const data = useContadorTablon()
  const n = Number(data?.por_firmar)
  return Number.isFinite(n) && n > 0 ? n : 0
}

// ── Y LO QUE ME HAN DEJADO Y NO HE ABIERTO ────────────────────────────────────────────────────
// EL AGUJERO QUE CIERRA, con nombre y apellidos: la nómina. El punto prometía «tienes algo que
// FIRMAR» y contaba solo eso, así que un documento personal que NO se firma —una nómina, un
// certificado, un justificante— dejaba la foto limpia. La persona se enteraba al entrar por su
// cuenta en Mi perfil, que es donde se entra tres veces al año: justo cuando ya no hace falta el
// aviso. Lo cuenta el servidor (`GET /tablon/contador` → `nuevos`) y solo cuenta lo PERSONAL: una
// circular para todo el equipo tiene su badge en el menú «Tablón», y no puede encender doce puntos
// cada vez que se cuelga un horario.
export function useNuevosEnMiPerfil() {
  const data = useContadorTablon()
  const m = Number(data?.nuevos)
  return Number.isFinite(m) && m > 0 ? m : 0
}

export default function AvatarPorFirmar({ children, style }) {
  const porFirmar = usePorFirmar()
  const nuevos = useNuevosEnMiPerfil()
  const n = porFirmar + nuevos
  // EL AVISO VA EN LA ENVOLTURA, NO EN EL PUNTO, y el punto no recoge el ratón (`pointerEvents`).
  // El punto se solapa con la esquina del avatar: si se quedara con el clic, pulsar ahí no abriría
  // el perfil y parecería que la aplicación se ha quedado colgada. Así el clic pasa siempre al
  // avatar de debajo, y el texto explicativo sale al pasar por encima del punto (el avatar tiene su
  // propio `title`, que manda en su parte).
  //
  // EL PUNTO DICE DE QUÉ VA, y no «tienes cosas». Firmar un contrato y mirar una nómina no son lo
  // mismo, así que cuando hay de las dos clases se nombran las dos: quien lo lee ya sabe si entra a
  // despachar una firma o solo a mirar.
  const trozos = []
  if (porFirmar > 0) trozos.push(porFirmar + (porFirmar === 1 ? ' documento pendiente' : ' documentos pendientes') + ' de firmar')
  if (nuevos > 0) trozos.push(nuevos + (nuevos === 1 ? ' documento nuevo' : ' documentos nuevos') + ' en tu perfil')
  const detalle = trozos.join(' y ')
  const aviso = t('components.AvatarPorFirmar.tienes_mi_perfil_mis_documentos', 'Tienes {{detalle}} (Mi perfil · Mis documentos)', { detalle })
  return (
    <span title={n > 0 ? aviso : undefined}
      style={{ position: 'relative', display: 'inline-flex', flexShrink: 0, ...style }}>
      {children}
      {n > 0 && (
        <span aria-label={detalle}
          style={{
            position: 'absolute', top: '-4px', right: '-4px', minWidth: '17px', height: '17px',
            padding: '0 4px', borderRadius: '9px', background: 'var(--warning)', color: '#fff',
            fontSize: '10.5px', fontWeight: 800, display: 'flex', alignItems: 'center',
            justifyContent: 'center', lineHeight: 1, border: '2px solid var(--surface)',
            pointerEvents: 'none',
          }}>
          {n > 9 ? '9+' : n}
        </span>
      )}
    </span>
  )
}
